import { formatDateTime } from "@/api/datetime";
import AgencyAuditLogs from "@/components/AgencyAuditLogs";
import { AddWorkerDialog } from "@/components/AddWorkerDialog";
import { useConfirm } from "@/components/DialogProvider";
import { InviteUserDialog } from "@/components/InviteUserDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlacesAutocomplete } from "@/components/ui/places-autocomplete";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAgency, useAgencyOperations } from "@/features/agencies/hooks/useAgencies";
import { useAgencyInvite } from "@/features/users/hooks/useAgencyInvite";
import { useUsers } from "@/features/users/hooks/useUsers";
import { UsersService } from "@/features/users/users-service";
import { useAgencyWorkers, useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { WorkersService } from "@/features/workers/workers-service";
import { storage } from "@/firebase";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Timestamp } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  ArrowLeft,
  Ban,
  Briefcase,
  Download,
  Edit2,
  FileText,
  Link as LinkIcon,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Trash2,
  Unlink,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const formatDate = (timestamp: Timestamp | Date | undefined | null) => {
  if (!timestamp) return "N/A";
  if (timestamp instanceof Timestamp) {
    return formatDateTime(timestamp.toDate());
  } else if (timestamp instanceof Date) {
    return formatDateTime(timestamp);
  }
  return "N/A";
};

export const Route = createFileRoute("/dashboard/admin/agencies/$agencyId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { agencyId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { agency, loading, error } = useAgency(agencyId);
  const { inviteData } = useAgencyInvite(agency?.email);
  const {
    updateAgency,
    updateAgencyEmail,
    addAttachment,
    removeAttachment,
    addContact,
    updateContact,
    removeContact,
    deleteAgency,
    loading: operationLoading,
    error: operationError,
  } = useAgencyOperations();
  const confirm = useConfirm();

  // Worker-related hooks
  const { workers: agencyWorkers, loading: workersLoading } = useAgencyWorkers(agencyId);
  const { workers: allWorkers } = useAllWorkers();
  const { users } = useUsers();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isAddWorkerDialogOpen, setIsAddWorkerDialogOpen] = useState(false);
  const [isAttachWorkerDialogOpen, setIsAttachWorkerDialogOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>("");
  const [newEmail, setNewEmail] = useState("");
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    emails: [""],
    phoneNumbers: [""],
    notes: "",
  });
  const [contactFormData, setContactFormData] = useState({
    id: "",
    title: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  const handleEditAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agency) return;

    try {
      const updates = {
        name: editFormData.name,
        email: editFormData.email.trim() || undefined,
        address: Object.values(editFormData.address).some((val) => val.trim() !== "")
          ? editFormData.address
          : undefined,
        emails: editFormData.emails.filter((email) => email.trim() !== ""),
        phoneNumbers: editFormData.phoneNumbers.filter((phone) => phone.trim() !== ""),
        notes: editFormData.notes,
      };

      await updateAgency(agency.id, updates);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating agency:", error);
    }
  };

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agency) return;

    try {
      await updateAgencyEmail(agency.id, newEmail);
      setIsEmailDialogOpen(false);
      setNewEmail("");
      toast.success("Agency email updated successfully");
    } catch (error) {
      console.error("Error updating email:", error);
      toast.error("Failed to update email");
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !agency) return;

    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      toast.info("File size must be less than 10MB");
      return;
    }

    const fileName = `${Date.now()}-${file.name}`;
    setUploadingFiles((prev) => [...prev, fileName]);

    try {
      const storageRef = ref(storage, `agencies/${agency.id}/attachments/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newAttachment = {
        id: fileName,
        label: file.name,
        fileName: file.name,
        fileType: file.type,
        fileUrl: downloadURL,
        uploadedAt: new Date(),
        size: file.size,
      };

      await addAttachment(agency.id, newAttachment);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.info("Failed to upload file");
    } finally {
      setUploadingFiles((prev) => prev.filter((f) => f !== fileName));
      // Reset file input
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId: string, fileUrl?: string) => {
    if (!agency) return;
    if (!(await confirm({ description: "Are you sure you want to delete this attachment?" })))
      return;

    try {
      // Delete from Firebase Storage if fileUrl is provided
      if (fileUrl) {
        try {
          const storageRef = ref(storage, fileUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn("Could not delete file from storage:", storageError);
        }
      }

      await removeAttachment(agency.id, attachmentId);
    } catch (error) {
      console.error("Error removing attachment:", error);
      toast.info("Failed to remove attachment");
    }
  };

  const openEditDialog = () => {
    if (!agency) return;

    setEditFormData({
      name: agency.name || "",
      email: agency.email || "",
      address: {
        street: agency.address?.street || "",
        city: agency.address?.city || "",
        state: agency.address?.state || "",
        zipCode: agency.address?.zipCode || "",
        country: agency.address?.country || "",
      },
      emails: agency.emails && agency.emails.length > 0 ? agency.emails : [""],
      phoneNumbers:
        agency.phoneNumbers && agency.phoneNumbers.length > 0 ? agency.phoneNumbers : [""],
      notes: agency.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  const openEmailDialog = () => {
    if (!agency) return;
    setNewEmail(agency.email || "");
    setIsEmailDialogOpen(true);
  };

  const openContactDialog = (contact?: any) => {
    if (contact) {
      setContactFormData(contact);
    } else {
      setContactFormData({
        id: "",
        title: "",
        email: "",
        phone: "",
        notes: "",
      });
    }
    setIsContactDialogOpen(true);
  };

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agency) return;

    try {
      if (contactFormData.id) {
        await updateContact(agency.id, contactFormData.id, contactFormData);
      } else {
        await addContact(agency.id, contactFormData);
      }
      setIsContactDialogOpen(false);
    } catch (error) {
      console.error("Error saving contact:", error);
    }
  };

  const handleRemoveContact = async (contactId: string) => {
    if (!agency) return;
    if (!(await confirm({ description: "Are you sure you want to delete this contact?" }))) return;

    try {
      await removeContact(agency.id, contactId);
    } catch (error) {
      console.error("Error removing contact:", error);
    }
  };

  const handleDeleteAgency = async () => {
    if (!agency) return;
    const confirmed = await confirm({
      title: "Delete agency",
      description:
        "This will permanently delete the agency and its related data. This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      await deleteAgency(agency.id);
      toast.success("Agency deleted successfully");
      navigate({ to: "/dashboard/admin/agencies" });
    } catch (err) {
      console.error("Error deleting agency:", err);
      toast.error("Failed to delete agency");
    }
  };

  const handleToggleAccessRevoked = async () => {
    if (!agency || agency.authless) return;

    const isRevoking = !agency.accessRevoked;

    const confirmed = await confirm({
      title: isRevoking ? "Revoke Access" : "Restore Access",
      description: isRevoking
        ? `Are you sure you want to revoke login access for ${agency.name}? They will be immediately signed out and unable to sign in.`
        : `Are you sure you want to restore login access for ${agency.name}?`,
      confirmText: isRevoking ? "Revoke" : "Restore",
      cancelText: "Cancel",
    });
    if (!confirmed) return;

    try {
      await UsersService.toggleAccess(agency.id, isRevoking);
      toast.success(isRevoking ? "Access revoked successfully" : "Access restored successfully");
    } catch (error: any) {
      toast.error(error.message || `Failed to ${isRevoking ? "revoke" : "restore"} access`);
      console.error("Error toggling access:", error);
    }
  };

  const addEmailField = () => {
    setEditFormData((prev) => ({
      ...prev,
      emails: [...prev.emails, ""],
    }));
  };

  const removeEmailField = (index: number) => {
    setEditFormData((prev) => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index),
    }));
  };

  const updateEmail = (index: number, value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      emails: prev.emails.map((email, i) => (i === index ? value : email)),
    }));
  };

  const addPhoneField = () => {
    setEditFormData((prev) => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, ""],
    }));
  };

  const removePhoneField = (index: number) => {
    setEditFormData((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index),
    }));
  };

  const updatePhone = (index: number, value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) => (i === index ? value : phone)),
    }));
  };

  // Worker management functions
  const getWorkerInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getManagerName = (managerId?: string) => {
    if (!managerId) return "No Manager";
    const manager = users?.find((user) => user.uid === managerId && user.type === "manager");
    return manager?.name || "Unknown Manager";
  };

  const handleAttachWorker = async () => {
    if (!selectedWorkerId || !agency) return;

    try {
      await WorkersService.updateWorkerAgency(selectedWorkerId, agency.id);
      toast.success("Worker attached to agency successfully");
      setIsAttachWorkerDialogOpen(false);
      setSelectedWorkerId("");
    } catch (error) {
      console.error("Error attaching worker:", error);
      toast.error("Failed to attach worker");
    }
  };

  const handleDetachWorker = async (workerId: string, workerName: string) => {
    if (!agency) return;

    const confirmed = await confirm({
      title: "Detach Worker",
      description: `Are you sure you want to detach ${workerName} from ${agency.name}?`,
      confirmText: "Detach",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      await WorkersService.updateWorkerAgency(workerId, null);
      toast.success("Worker detached from agency successfully");
    } catch (error) {
      console.error("Error detaching worker:", error);
      toast.error("Failed to detach worker");
    }
  };

  const handleWorkerAdded = (workerId?: string) => {
    setIsAddWorkerDialogOpen(false);
    if (workerId) {
      toast.success("Worker created and attached to agency successfully");
    }
  };

  // Get workers that are not attached to this agency
  const availableWorkersToAttach = allWorkers.filter(
    (worker) => worker.agencyId !== agencyId && worker.isDeleted !== true,
  );

  if (loading) {
    return (
      <div className="container mx-auto space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const router = useRouter();

  if (error || !agency) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center gap-4">
          <Button onClick={() => router.history.back()} variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertDescription>{error?.message || "Agency not found"}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 p-1 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4">
          <Button
            onClick={() => router.history.back()}
            variant="ghost"
            size="sm"
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{agency.name}</h1>
              {!agency.authless && (
                <Badge
                  variant={agency.accessRevoked ? "destructive" : "secondary"}
                  className="w-fit text-xs"
                >
                  {agency.accessRevoked ? "Access Revoked" : "Has login access"}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              Agency since {formatDate(agency.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {agency.authless && agency.email && (
            <InviteUserDialog
              defaultEmail={agency.email}
              defaultRole="agency"
              defaultRoles={["agency"]}
              agencyId={agency.id}
              triggerButton={
                <Button variant="outline" className="w-full sm:w-auto">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite Agency
                </Button>
              }
            />
          )}
          {agency.authless && !agency.email && (
            <Button
              variant="outline"
              onClick={() => {
                toast.info("Please set an email address before inviting this agency");
                openEmailDialog();
              }}
              className="w-full sm:w-auto"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Agency
            </Button>
          )}
          {!agency.authless && (
            <Button
              onClick={handleToggleAccessRevoked}
              variant="outline"
              className={`w-full sm:w-auto ${
                agency.accessRevoked
                  ? "text-green-600 hover:bg-green-50 hover:text-green-700"
                  : "text-orange-600 hover:bg-orange-50 hover:text-orange-700"
              }`}
              title={agency.accessRevoked ? "Restore Access" : "Revoke Access"}
            >
              <Ban className="mr-2 h-4 w-4" />
              {agency.accessRevoked ? "Restore Access" : "Revoke Access"}
            </Button>
          )}
          <Button variant="outline" onClick={openEditDialog} className="w-full sm:w-auto">
            <Edit2 className="mr-2 h-4 w-4" />
            Edit Agency
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteAgency}
            disabled={operationLoading}
            className="w-full sm:w-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Agency
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Agency Details</TabsTrigger>
          <TabsTrigger value="workers">
            Workers ({agencyWorkers.length})
          </TabsTrigger>
          <TabsTrigger value="audit-logs">Audit Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <Label className="text-muted-foreground text-sm font-medium">
                      Primary Email
                    </Label>
                    <Button variant="ghost" size="sm" onClick={openEmailDialog}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {agency.email ? (
                    <a
                      href={`mailto:${agency.email}`}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {agency.email}
                    </a>
                  ) : (
                    <p className="text-muted-foreground text-sm">No primary email set</p>
                  )}
                </div>

                {inviteData && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Invitation Status
                    </Label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={inviteData.status === "pending" ? "secondary" : "default"}
                          className="capitalize"
                        >
                          {inviteData.status}
                        </Badge>
                      </div>
                      {inviteData.invitedAt && (
                        <div>
                          <p className="text-muted-foreground text-xs">Invited on</p>
                          <p className="text-sm">{formatDate(inviteData.invitedAt)}</p>
                        </div>
                      )}
                      {inviteData.acceptedAt && (
                        <div>
                          <p className="text-muted-foreground text-xs">Invitation accepted on</p>
                          <p className="text-sm">{formatDate(inviteData.acceptedAt)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {agency.emails && agency.emails.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Additional Emails
                    </Label>
                    <div className="space-y-1">
                      {agency.emails.map((email, index) => (
                        <a
                          key={index}
                          href={`mailto:${email}`}
                          className="block text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {email}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {agency.phoneNumbers && agency.phoneNumbers.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Phone Numbers
                    </Label>
                    <div className="space-y-1">
                      {agency.phoneNumbers.map((phone, index) => (
                        <a
                          key={index}
                          href={`tel:${phone}`}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          <Phone className="h-4 w-4" />
                          {formatPhoneNumber(phone)}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {(!agency.emails || agency.emails.length === 0) &&
                  (!agency.phoneNumbers || agency.phoneNumbers.length === 0) && (
                    <p className="text-muted-foreground text-sm">No contact information provided</p>
                  )}
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                {agency.address && Object.values(agency.address).some((val) => val) ? (
                  <div className="space-y-2">
                    {agency.address.street && <p className="text-sm">{agency.address.street}</p>}
                    <p className="text-sm">
                      {[agency.address.city, agency.address.state].filter(Boolean).join(", ")}
                      {agency.address.zipCode && ` ${agency.address.zipCode}`}
                    </p>
                    {agency.address.country && <p className="text-sm">{agency.address.country}</p>}
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        [
                          agency.address.street,
                          agency.address.city,
                          agency.address.state,
                          agency.address.zipCode,
                          agency.address.country,
                        ]
                          .filter(Boolean)
                          .join(", "),
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <MapPin className="h-4 w-4" />
                      Open in Google Maps
                    </a>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No address provided</p>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {agency.notes ? (
                  <p className="text-sm whitespace-pre-wrap">{agency.notes}</p>
                ) : (
                  <p className="text-muted-foreground text-sm">No notes available</p>
                )}
              </CardContent>
            </Card>

            {/* Contact People */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Contact People
                  </CardTitle>
                  <Button size="sm" onClick={() => openContactDialog()}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add Contact
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {agency.contacts && agency.contacts.length > 0 ? (
                    agency.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{contact.title}</p>
                          <div className="text-muted-foreground flex items-center gap-2 truncate text-xs">
                            {contact.email && (
                              <a
                                href={`mailto:${contact.email}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {contact.email}
                              </a>
                            )}
                            {contact.email && contact.phone && <span>-</span>}
                            {contact.phone && (
                              <a
                                href={`tel:${contact.phone}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {formatPhoneNumber(contact.phone)}
                              </a>
                            )}
                          </div>
                          {contact.notes && (
                            <p className="text-muted-foreground truncate text-xs">
                              {contact.notes}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openContactDialog(contact)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveContact(contact.id)}
                            disabled={operationLoading}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      No contacts yet. Add a contact to get started.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Attachments</CardTitle>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="attachment-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
                    />
                    <Button
                      size="sm"
                      onClick={() => document.getElementById("attachment-upload")?.click()}
                      disabled={uploadingFiles.length > 0}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload File
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {uploadingFiles.map((fileName) => (
                    <div
                      key={fileName}
                      className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3"
                    >
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      <span className="text-muted-foreground text-sm">Uploading {fileName}...</span>
                    </div>
                  ))}

                  {agency.attachments && agency.attachments.length > 0
                    ? agency.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <div className="min-w-0 flex-1">
                              {attachment.fileUrl ? (
                                <a
                                  href={attachment.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cursor-pointer truncate text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                  title="Click to open in new tab"
                                >
                                  {attachment.label}
                                </a>
                              ) : (
                                <p className="truncate text-sm font-medium">{attachment.label}</p>
                              )}
                              {attachment.fileName && (
                                <p className="text-muted-foreground truncate text-xs">
                                  {attachment.fileName}
                                </p>
                              )}
                              {attachment.uploadedAt && (
                                <p className="text-muted-foreground text-xs">
                                  Uploaded {formatDate(attachment.uploadedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {attachment.fileUrl && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(attachment.fileUrl, "_blank")}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleRemoveAttachment(attachment.id, attachment.fileUrl)
                              }
                              disabled={operationLoading}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    : uploadingFiles.length === 0 && (
                        <p className="text-muted-foreground py-4 text-center text-sm">
                          No attachments yet. Upload some files to get started.
                        </p>
                      )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="workers">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Workers
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsAttachWorkerDialogOpen(true)}
                    disabled={availableWorkersToAttach.length === 0}
                  >
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Attach Worker
                  </Button>
                  <Button size="sm" onClick={() => setIsAddWorkerDialogOpen(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create Worker
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {workersLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : agencyWorkers.length > 0 ? (
                <div className="space-y-2">
                  {agencyWorkers.map((worker) => (
                    <div
                      key={worker.id}
                      className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          {worker.profilePicture && (
                            <AvatarImage src={worker.profilePicture} alt={worker.name} />
                          )}
                          <AvatarFallback>{getWorkerInitials(worker.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Link
                              to="/dashboard/admin/workers/$workerId"
                              params={{ workerId: worker.id }}
                              className="truncate text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {worker.name}
                            </Link>
                            {worker.isTestNumber && (
                              <Badge variant="secondary" className="text-xs">
                                Test
                              </Badge>
                            )}
                            {worker.type === "app_worker" && (
                              <Badge variant="outline" className="text-xs">
                                App Worker
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                            <a
                              href={`tel:${worker.phoneNumber}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {formatPhoneNumber(worker.phoneNumber)}
                            </a>
                            {worker.managerId && (
                              <>
                                <span>•</span>
                                <span>{getManagerName(worker.managerId)}</span>
                              </>
                            )}
                            {worker.specialties && worker.specialties.length > 0 && (
                              <>
                                <span>•</span>
                                <span>{worker.specialties.join(", ")}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDetachWorker(worker.id, worker.name)}
                        title="Detach worker from agency"
                      >
                        <Unlink className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  No workers attached to this agency yet. Create or attach workers to get started.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit-logs">
          <AgencyAuditLogs agencyId={agencyId} />
        </TabsContent>
      </Tabs>

      {/* Edit Agency Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Agency</DialogTitle>
            <DialogDescription>Update agency information and contact details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditAgency}>
            <div className="grid gap-4 py-4">
              {/* Name */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">
                  Name *
                </Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="col-span-3"
                  required
                />
              </div>

              {/* Primary Email */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-email" className="text-right">
                  Primary Email
                </Label>
                <div className="col-span-3 space-y-1">
                  <Input
                    id="edit-email"
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  />
                  <p className="text-muted-foreground text-xs">
                    Used for login credentials if agency is invited
                  </p>
                </div>
              </div>

              {/* Additional Emails */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Additional Emails</Label>
                <div className="col-span-3 space-y-2">
                  {editFormData.emails.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => updateEmail(index, e.target.value)}
                        placeholder="agency@email.com"
                      />
                      {editFormData.emails.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeEmailField(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addEmailField}>
                    Add Email
                  </Button>
                </div>
              </div>

              {/* Phone Numbers */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Phone Numbers</Label>
                <div className="col-span-3 space-y-2">
                  {editFormData.phoneNumbers.map((phone, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => updatePhone(index, e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                      {editFormData.phoneNumbers.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removePhoneField(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addPhoneField}>
                    Add Phone
                  </Button>
                </div>
              </div>

              {/* Address */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Address</Label>
                <div className="col-span-3 space-y-2">
                  <PlacesAutocomplete
                    value={editFormData.address.street}
                    onChange={(value) =>
                      setEditFormData({
                        ...editFormData,
                        address: {
                          ...editFormData.address,
                          street: value,
                        },
                      })
                    }
                    placeholder="Search for street address..."
                    types="geocode"
                    language="en"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="City"
                      value={editFormData.address.city}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            city: e.target.value,
                          },
                        })
                      }
                    />
                    <Input
                      placeholder="State"
                      value={editFormData.address.state}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            state: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Zip Code"
                      value={editFormData.address.zipCode}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            zipCode: e.target.value,
                          },
                        })
                      }
                    />
                    <Input
                      placeholder="Country"
                      value={editFormData.address.country}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            country: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="edit-notes" className="pt-2 text-right">
                  Notes
                </Label>
                <Textarea
                  id="edit-notes"
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  className="col-span-3"
                  placeholder="Any additional notes about this agency..."
                  rows={3}
                />
              </div>
            </div>

            {operationError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{operationError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={operationLoading}>
                {operationLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Agency"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Primary Email</DialogTitle>
            <DialogDescription>
              Update the primary email address for this agency. This email will be used for login
              credentials if the agency is invited.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateEmail}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="new-email" className="text-right">
                  Email
                </Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={operationLoading}>
                {operationLoading ? "Updating..." : "Update Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{contactFormData.id ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>
              {contactFormData.id
                ? "Update the contact person's details."
                : "Add a new contact person for this agency."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveContact}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-title" className="text-right">
                  Title
                </Label>
                <Input
                  id="contact-title"
                  value={contactFormData.title}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      title: e.target.value,
                    })
                  }
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-email" className="text-right">
                  Email
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactFormData.email}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      email: e.target.value,
                    })
                  }
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-phone" className="text-right">
                  Phone
                </Label>
                <Input
                  id="contact-phone"
                  value={contactFormData.phone}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      phone: e.target.value,
                    })
                  }
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-notes" className="text-right">
                  Notes
                </Label>
                <Textarea
                  id="contact-notes"
                  value={contactFormData.notes}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      notes: e.target.value,
                    })
                  }
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={operationLoading}>
                {operationLoading ? "Saving..." : "Save Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Worker Dialog */}
      <AddWorkerDialog
        open={isAddWorkerDialogOpen}
        onOpenChange={setIsAddWorkerDialogOpen}
        agencyId={agencyId}
        showManagerField={true}
        showPayRateField={true}
        showBillingRateField={true}
        showSpecialtiesField={true}
        managers={users?.filter((user) => user.type === "manager") || []}
        onSuccess={handleWorkerAdded}
      />

      {/* Attach Worker Dialog */}
      <Dialog open={isAttachWorkerDialogOpen} onOpenChange={setIsAttachWorkerDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Attach Worker to Agency</DialogTitle>
            <DialogDescription>
              Select a worker to attach to {agency?.name}. Workers already attached to this agency
              are not shown.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Worker</Label>
            <SearchableSelectPopover
              items={availableWorkersToAttach}
              selectedValue={selectedWorkerId}
              onSelect={(value) => setSelectedWorkerId(value || "")}
              getKey={(worker) => worker.id}
              getLabel={(worker) =>
                `${worker.name} - ${formatPhoneNumber(worker.phoneNumber)}${
                  worker.agency ? ` (${worker.agency.name})` : ""
                }`
              }
              triggerLabel={
                selectedWorkerId
                  ? availableWorkersToAttach.find((w) => w.id === selectedWorkerId)?.name ||
                    "Select Worker"
                  : "Select Worker"
              }
              placeholder="Search workers..."
              emptyText="No available workers found"
              buttonClassName="w-full justify-between"
              contentClassName="w-full p-0"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAttachWorkerDialogOpen(false);
                setSelectedWorkerId("");
              }}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleAttachWorker} disabled={!selectedWorkerId}>
              Attach Worker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
